<template>
  <div class="tna-page">
    <v-row>
      <!-- Left Section -->
      <v-col
        cols="3"
        class="py-0 pl-0">
        <div class="tna-card pa-4">
          <h2 class="mb-4">
            Collection
          </h2>
          <div
            v-if="initLoading"
            class="pa-4 text-center"
          >
            <v-progress-circular
              indeterminate
              size="24" />
          </div>
          <CollectionSearch
            v-else
            :follow-device-timezone="true"
            :filter-for-production-plan-v3="true"
            :preselected-id="$route.params.collectionId"
            @onSelect="onSelectCollection" />
        </div>
      </v-col>

      <!-- Right Section -->
      <v-col
        cols="9"
        class="py-0 pr-0">
        <img-popup-preview
          v-model="previewImgEnabled"
          :img-src="currentImgPreview" />
        <div
          class="tna-card d-flex flex-column"
          style="height: calc(100vh - 80px); overflow: hidden;"
        >
          <div class="tna-header">
            <div>
              <h2 class="mb-0">
                Time and Action
              </h2>
              <span
                v-if="selectedCollection"
                class="tna-subtitle">
                {{ selectedCollection.name }}
                <template v-if="selectedCollection.salesDate">&middot; {{ selectedCollection.salesDate | salesDateInLocalTimeZone }}</template>
                <template v-else>&middot; ไม่มี Sale Date</template>
                &middot; Dates follow your local device timezone
              </span>
              <span
                v-if="isCopyMode"
                class="tna-subtitle">
                กำลัง copy timeline จาก <b>{{ get(copySourcePrototype, 'sketch.sketchId') }}</b>
              </span>
            </div>
            <div class="tna-header-actions">
              <div
                v-if="selectedCollection"
                class="tna-stock-value">
                <div class="tna-stock-label">
                  Stock Value
                </div>
                <div class="tna-stock-amount">
                  <span v-if="initLoading || loadingPrototype">-</span>
                  <span v-else>{{ currentStockValue | showNumberFormat }}</span>
                  <span class="tna-stock-target"> / {{ selectedCollection.targetStockValue | showNumberFormat }}</span>
                </div>
              </div>
              <v-btn
                v-if="isCopyMode"
                text
                @click="cancelCopyTimeline">
                CANCEL
              </v-btn>
              <v-btn
                v-if="isCopyMode && copyTargetIds.length"
                color="primary"
                @click="openCopyNoteDialog">
                PASTE ({{ copyTargetIds.length }})
              </v-btn>
              <v-btn
                :disabled="!prototypes.length"
                outlined
                color="green"
                @click="exportCSV">
                EXPORT
              </v-btn>
              <v-btn
                v-if="isControllerUser"
                :disabled="!prototypes.length"
                outlined
                color="green"
                @click="exportForUpdate">
                EXPORT FOR UPDATE
              </v-btn>
              <v-btn
                v-if="isControllerUser"
                :disabled="!prototypes.length"
                outlined
                color="primary"
                @click="importTaskDateDialog = true">
                IMPORT
              </v-btn>
            </div>
          </div>

          <div
            v-if="!initLoading && !loadingPrototype && workflow && selectedCollection && prototypes.length"
            class="tna-legend">
            <span class="tna-legend-title">Status</span>
            <div
              v-for="item in legend"
              :key="item.label"
              class="tna-legend-item">
              <span
                class="tna-legend-swatch"
                :class="item.style.bgClass"></span>
              <span class="tna-legend-label">{{ item.label }}</span>
            </div>
          </div>

          <div style="flex: 1; min-height: 0; overflow: hidden; padding: 8px;">
            <div
              v-if="initLoading || loadingPrototype"
              class="pa-4 text-center"
            >
              <v-progress-circular
                indeterminate
                size="24" />
            </div>
            <div
              v-else-if="!workflow"
              class="pa-4 text-center">
              กรุณาสร้าง workflow ก่อนดำเนินการต่อ
            </div>
            <div
              v-else-if="!selectedCollection"
              class="pa-4 text-center">
              กรุณาเลือก Collection
            </div>
            <div
              v-else-if="prototypes.length"
              class="tna-table-scroll">
              <table
                class="table">
                <thead>
                  <tr>
                    <th
                      v-for="header in headers"
                      :key="header.text"
                      :class="`${header.classCss}`"
                      class="text-center"
                      :style="`min-width: ${header.width}px; white-space: nowrap;`"
                    >
                      <v-checkbox
                        v-if="header.text === 'Image' && isCopyMode"
                        :input-value="allTargetsSelected"
                        :indeterminate="someTargetsSelected && !allTargetsSelected"
                        hide-details
                        dense
                        class="ma-0 pa-0 d-inline-block"
                        title="เลือกทั้งหมด"
                        @change="toggleSelectAllTargets"
                      />
                      <template v-else>
                        {{ header.text }}
                      </template>
                      <v-icon
                        v-if="header.stageId && isControllerUser"
                        small
                        color="primary"
                        class="ml-1"
                        style="cursor: pointer;"
                        @click="openStageDateDialog(header.stageId)"
                      >
                        mdi-calendar-edit
                      </v-icon>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="prototype in prototypes"
                    :key="prototype.id"
                    class="text-center"
                    :class="{ 'source-row': prototype.id === copySourceId }"
                  >
                    <td
                      align="center"
                      class="sticky-col col-0 tna-status-cell"
                      :class="worstStyleFor(prototype) ? worstStyleFor(prototype).bgClass : ''">
                    </td>

                    <td
                      align="center"
                      class="sticky-col col-1">
                      <div class="d-flex align-center justify-center">
                        <v-checkbox
                          v-if="isCopyMode && prototype.id !== copySourceId"
                          :input-value="copyTargetIds.includes(prototype.id)"
                          hide-details
                          dense
                          class="ma-0 pa-0 mr-1"
                          @change="toggleCopyTarget(prototype.id)"
                        />
                        <img
                          v-if="getPhotoUrl(prototype) !== ''"
                          :src="getPhotoUrl(prototype)"
                          class="elevation-1 main-image"
                          alt="product-image"
                          @click="previewImgEnabled = true ,currentImgPreview = getPhotoUrl(prototype)">
                        <img
                          v-else
                          :src="noImgAvailable"
                          class="elevation-1 main-image"
                          alt="product-image">
                      </div>
                    </td>

                    <td
                      class="sticky-col col-2">
                      <v-row>
                        <v-col>
                          <router-link
                            class="text-bold"
                            :to="prototype.state === 'sketch'
                              ? `/prototype/sketch/edit/${prototype.id}`
                              : `/prototype/order/edit/${prototype.id}`"
                            target="_blank"
                          >
                            {{ get(prototype, 'sketch.sketchId') }}
                          </router-link>
                          <v-icon
                            v-if="hasChangedDateTask(prototype)"
                            small
                            color="orange"
                            class="ml-1"
                            title="มี task ที่แก้ไขวันที่"
                          >
                            mdi-pencil-circle-outline
                          </v-icon>
                        </v-col>
                      </v-row>
                      <v-icon
                        v-if="isCanSelectTimeline"
                        small
                        :color="prototype.id === copySourceId ? 'primary' : 'grey darken-1'"
                        class="ml-1"
                        style="cursor: pointer;"
                        title="Copy Timeline"
                        @click="toggleCopySource(prototype)"
                      >
                        mdi-content-copy
                      </v-icon>
                      <v-icon
                        v-if="isControllerUser"
                        small
                        color="primary"
                        class="ml-1"
                        style="cursor: pointer;"
                        title="Gantt Chart"
                        @click="$router.push({ name: 'ProductionPlanGantt', params: { prototypeId: String(prototype.id) } })"
                      >
                        mdi-chart-gantt
                      </v-icon>
                    </td>
                    <td width="200">
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="resolveFactory(prototype)"
                          dense
                          :items="selectOptions.factories"
                          :menu-props="{ offsetY: true }"
                          label="Factory"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'factory', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <confirm-select
                          v-model="prototype.timelineId"
                          :disabled="!isCanSelectTimeline"
                          label="Timeline"
                          :items="activeTimelines"
                          item-text="name"
                          item-value="id"
                          @confirm="(newTimelineId) =>
                            assignTimeline(
                              prototype.id,
                              prototype.sketch.sketchId,
                              newTimelineId
                            )
                          "
                        />
                      </div>
                    </td>
                    <td>
                      <div
                        class="tna-pill-select"
                        :style="deliveryPillStyle(prototype.deliveryStatus)">
                        <v-select
                          :value="prototype.deliveryStatus"
                          label="Delivery Status"
                          outlined
                          dense
                          item-text="text"
                          item-value="value"
                          :items="selectOptions.deliveryStatus"
                          @change="val => updateDeliveryStatus(prototype.id, val)"></v-select>
                      </div>
                    </td>
                    <!-- Dynamic stage columns -->
                    <td
                      v-for="stage in workflow.stages"
                      :key="stage.id"
                      class="text-center pa-1 td-cell"
                    >
                      <div class="td-flow">
                        <div
                          v-if="prototype.tasksByStage[stage.id]"
                          :class="prototype.tasksByStageDisplay[stage.id].bgClass"
                          :style="stageCardStyle(prototype.tasksByStageDisplay[stage.id].status)"
                          class="tna-stage-card align-center pa-2 ma-1 td-fill"
                        >
                          <v-row no-gutters>
                            <!-- ICON -->
                            <v-col
                              cols="2"
                              class="d-flex align-center justify-center">
                              <v-icon
                                v-if="prototype.tasksByStageDisplay[stage.id].status === 'Completed'"
                                color="green"
                                @click="onUnComplete(prototype.id, prototype.tasksByStage[stage.id].id)"
                              >
                                mdi-check-circle
                              </v-icon>

                              <v-icon
                                v-else
                                color="white darken-3"
                                @click="updateTaskStatus(
                                  prototype.id,
                                  prototype.tasksByStage[stage.id].id,
                                  prototype.tasksByStageDisplay[stage.id].taskStatusOnComplete
                                )"
                              >
                                mdi-circle
                              </v-icon>
                            </v-col>

                            <!-- CONTENT -->
                            <v-col
                              cols="10"
                              class="text-left pl-2">
                              <h3
                                class="mb-1 font-weight-bold"
                                :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                              >
                                {{ prototype.tasksByStageDisplay[stage.id].status }}
                                <span
                                  v-if="prototype.tasksByStageDisplay[stage.id].extendText"
                                  :class="prototype.tasksByStageDisplay[stage.id].textColor2"
                                >
                                  ({{ prototype.tasksByStageDisplay[stage.id].extendText }})
                                </span>
                              </h3>
                              <v-row no-gutters>
                                <v-col cols="8">
                                  <!-- Start -->
                                  <p
                                    v-if="prototype.tasksByStage[stage.id].completedAt"
                                    class="mb-1"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1">
                                    Due:
                                    <span :class="prototype.tasksByStageDisplay[stage.id].textColor1">
                                      {{ prototype.tasksByStage[stage.id].dueDate | dateUserFormat }}
                                    </span>
                                  </p>
                                  <a
                                    v-else-if="isControllerUser"
                                    class="mb-1 d-block"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                                    @click="!prototype.tasksByStage[stage.id].completedAt &&
                                      openDialogTaskDate(prototype.id, prototype.tasksByStage[stage.id], 'startDate')"
                                  >
                                    Start: <span class="text-decoration-underline">
                                      {{ prototype.tasksByStage[stage.id].startDate | dateUserFormat }}
                                    </span>
                                  </a>
                                  <p
                                    v-else
                                    class="mb-1"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                                  >
                                    Start: <span>
                                      {{ prototype.tasksByStage[stage.id].startDate | dateUserFormat }}
                                    </span>
                                  </p>

                                  <!-- Done / Due -->
                                  <p
                                    v-if="prototype.tasksByStage[stage.id].completedAt"
                                    class="mb-1"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                                  >
                                    Done:
                                    <span :class="prototype.tasksByStageDisplay[stage.id].textColor2">
                                      {{ prototype.tasksByStage[stage.id].completedAt | dateUserFormat }}
                                    </span>
                                  </p>

                                  <a
                                    v-else-if="isControllerUser"
                                    class="mb-1 d-block"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                                    @click="openDialogTaskDate(prototype.id, prototype.tasksByStage[stage.id], 'dueDate')"
                                  >
                                    Due:
                                    <span
                                      :class="prototype.tasksByStageDisplay[stage.id].textColor2"
                                      class="text-decoration-underline">
                                      {{ prototype.tasksByStage[stage.id].dueDate | dateUserFormat }}
                                    </span>
                                  </a>
                                  <p
                                    v-else
                                    class="mb-1"
                                    :class="prototype.tasksByStageDisplay[stage.id].textColor1"
                                  >
                                    Due:
                                    <span
                                      :class="prototype.tasksByStageDisplay[stage.id].textColor2">
                                      {{ prototype.tasksByStage[stage.id].dueDate | dateUserFormat }}
                                    </span>
                                  </p>
                                </v-col>
                                <v-col
                                  cols="4"
                                  class="d-flex align-center">
                                  <v-icon
                                    v-if="prototype.tasksByStage[stage.id].changeDateAt"
                                    color="orange"
                                    size="medium">
                                    mdi-pencil-circle-outline
                                  </v-icon>
                                </v-col>
                              </v-row>

                              <p
                                class="mb-0"
                                :class="prototype.tasksByStageDisplay[stage.id].textColor1">
                                Working Days:
                                <span :class="prototype.tasksByStageDisplay[stage.id].textColor2">
                                  {{ prototype.tasksByStage[stage.id].workingDays }}
                                </span>
                              </p>
                            </v-col>
                          </v-row>
                        </div>

                        <!-- NO TASK -->
                        <div
                          v-else
                          class="tna-stage-card tna-stage-card--empty ma-1 text-caption d-flex align-center justify-center td-fill"
                        >
                          <span class="grey--text">No Task</span>
                        </div>
                      </div>
                    </td>

                    <!-- Extra tail columns -->
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.designer"
                          dense
                          :items="designersWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Designer"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'designer', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.merchandiser"
                          dense
                          :items="merchandisersWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Merchandiser"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'merchandiser', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.graphic"
                          dense
                          :items="graphicsWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Graphic"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'graphic', val)">
                        </v-autocomplete>
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.stylist"
                          dense
                          :items="creativesWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Creative / Stylist"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'stylist', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.visualMerchandiser"
                          dense
                          :items="visualMerchandiserWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Visual Merchandiser"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'visualMerchandiser', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.marketing"
                          dense
                          :items="marketingsWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Marketing"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'marketing', val)" />
                      </div>
                    </td>
                    <td>
                      <div class="tna-pill-select">
                        <v-autocomplete
                          :value="prototype.sketch.producer"
                          dense
                          :items="producersWithHeader"
                          :menu-props="{ offsetY: true }"
                          label="Producer"
                          item-text="name"
                          item-value="id"
                          return-object
                          outlined
                          hide-details
                          @change="(val) => updateSketchAssignee(prototype.id, 'producer', val)" />
                      </div>
                    </td>
                    <td class="tna-stock-cell">
                      {{ prototype.stockValue | showNumberFormat }}
                    </td>
                    <td>
                      <div
                        v-for="(po, index) in prototype.purchaseOrders"
                        :key="po.id">
                        <span
                          class="po-link"
                          @click="showPo(po.id)"
                        >{{ po.orderNo }}</span>{{ poQtyText(po, get(prototype, 'sketch.sketchId')) }}<span
                          v-if="index !== prototype.purchaseOrders.length - 1"
                        >, </span>
                      </div>
                    </td>

                    <!-- Custom field columns -->
                    <td
                      v-for="field in customFields"
                      :key="field.id">
                      <div>
                        <v-text-field
                          :value="getCustomFieldValue(prototype.id, field.id)"
                          type="text"
                          :label="field.label || field.name"
                          :placeholder="field.label || field.name"
                          dense
                          outlined
                          hide-details
                          @input="(val) => updateCustomFieldValue(prototype.id, field.id, val)"
                          @click:clear="updateCustomFieldValue(prototype.id, field.id, '')"
                          @keyup.enter="saveCustomField(prototype, field)">
                          <template #append>
                            <v-icon
                              v-if="isCustomFieldDirty(prototype.id, field.id)"
                              color="secondary"
                              title="บันทึก"
                              @click.stop="saveCustomField(prototype, field)">
                              mdi-content-save
                            </v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-else
              class="pa-4 text-center">
              ไม่พบ Prototype ใน Collection นี้
            </div>
          </div>
        </div>
        <update-task-date-dialog
          v-model="dialogUpdateDate"
          :task-id="dialogUpdateTaskDateInfo.taskId"
          :prototype-id="dialogUpdateTaskDateInfo.prototypeId"
          :date-type="dialogUpdateTaskDateInfo.dateType"
          :date-change="dialogUpdateTaskDateInfo.dateChange"
          @confirm="handleUpdateDate"
          @close="clearDialogUpdateTaskDateInfo"
        />
        <update-stage-date-dialog
          v-model="dialogBulkStageDate"
          :stages="workflow ? workflow.stages : []"
          :pre-selected-stage-id="stagePreSelectedId"
          @confirm="handleStageDateUpdate"
          @close="stagePreSelectedId = ''"
        />
        <task-date-import
          v-if="importTaskDateDialog"
          :prototypes="prototypes"
          :stage-columns="stageColumns"
          :loading="importTaskDateSaving"
          @confirm="handleImportTaskDate"
          @close="importTaskDateDialog = false"
        />
        <v-dialog
          v-model="copyNoteDialog"
          max-width="420"
          :persistent="false">
          <v-card>
            <v-card-title class="headline">
              Copy Timeline
            </v-card-title>
            <v-card-text>
              <p>
                Copy timeline จาก <b>{{ get(copySourcePrototype, 'sketch.sketchId') }}</b>
                ไปยัง <b>{{ copyTargetIds.length }}</b> sketch
              </p>
              <v-textarea
                v-model="copyNote"
                label="Note"
                rows="3"
                auto-grow
                outlined
                placeholder="Add a note"
                :rules="[v => !!v || 'กรุณาระบุโน๊ต']"
              />
            </v-card-text>
            <v-divider />
            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="copyNoteDialog = false">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!copyNote || copySaving"
                :loading="copySaving"
                @click="confirmCopyTimeline">
                Confirm
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import PrototypeProvider from '@/resources/PrototypeProvider'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import CollectionSearch from '@/components/CollectionsSearch.vue'
import NoImgAvailable from '@/assets/image/no_picture_available.png'
import FactoryProvider from '@/resources/FactoryProvider'
import PrototypeAttributeProvider from '@/resources/PrototypeAttributeProvider'
import ImgPopupPreview from '@/components/ImgModalPreview.vue'
import UpdateTaskDateDialog from '../components/UpdateTaskDateDialog.vue'
import UpdateStageDateDialog from '../components/UpdateStageDateDialog.vue'
import ConfirmSelect from '../components/ConfirmSelect.vue'
import TaskDateImport from '../components/TaskDateImport.vue'
import { mapActions } from 'vuex'
import calculateTaskStatus from '@/assets/js/CalculateTaskStatus'
import TASK_STATUS_STYLES from '@/assets/js/TaskStatusStyles'
import { ExportToCsv } from 'export-to-csv'
import { capitalize } from '@/assets/js/StringFormat'
import getPhotoUrl from '@/assets/js/PhotoUrl'
import { getRole } from '@/assets/js/Authentication'

const FactoryService = new FactoryProvider()
const PrototypeAttributeService = new PrototypeAttributeProvider()
const PrototypeService = new PrototypeProvider()
const ProductionPlanService = new ProductionPlanProvider()

const OPEN_SEVERITY = ['critical', 'overdue', 'dueIn0', 'dueIn1', 'dueIn2', 'inProgress', 'pending']

export default {
  components: {
    CollectionSearch,
    ImgPopupPreview,
    UpdateTaskDateDialog,
    UpdateStageDateDialog,
    ConfirmSelect,
    TaskDateImport
  },
  data () {
    return {
        prototypeService: PrototypeService,
        productionPlanService: ProductionPlanService,
        selectedCollection: null,
        prototypes: [],
        workflow: null,
        headers: [
            {
                text: '',
                value: '',
                align: 'center',
                width: 30,
                classCss: 'sticky-col col-0'
            },
            {
                text: 'Image',
                value: 'sketch.images[0]',
                align: 'center',
                width: 100,
                classCss: 'sticky-col col-1'
            },
            {
                text: 'Sketch ID',
                value: 'sketch.sketchId',
                align: 'center',
                width: 115,
                classCss: 'sticky-col col-2'
            },
            {
                text: 'Factory',
                value: 'sketch.factory.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Timeline',
                value: 'timeline.name',
                align: 'center',
                width: 200
            },
            {
              text: 'Delivery Status',
              value: '',
              width: 200
            }
        ],
        tailHeaders: [
            {
                text: 'Designer',
                value: 'sketch.designer.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Merchandiser',
                value: 'sketch.merchandiser.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Graphic',
                value: 'sketch.graphic.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Creative / Stylist',
                value: 'sketch.stylist.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Visual Merchandiser',
                value: 'sketch.visualMerchandiser.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Marketing',
                value: 'sketch.marketing.name',
                align: 'center',
                width: 200
            },
            {
                text: 'Producer',
                value: 'sketch.producer.name',
                align: 'center',
                width: 200
            },
            {
              text: 'Stock Value',
              value: '',
              width: 200
          },
          {
              text: 'PO',
              value: 'purchaseOrders',
              width: 300
          }
        ],
        status: TASK_STATUS_STYLES,
        initLoading: false,
        loadingPrototype: false,
        noImgAvailable: NoImgAvailable,
        selectOptions: {
          factories: [],
          designers: [],
          merchandisers: [],
          graphics: [],
          stylists: [],
          marketings: [],
          visualMerchandisers: [],
          producers: [],
          deliveryStatus: [
            { text: 'ไม่ระบุ', value: '' },
            { text: 'On Time', value: 'on_time' },
            { text: 'Within Month', value: 'within_month' },
            { text: 'Late', value: 'late' }
          ]
        },
        previewImgEnabled: false,
        currentImgPreview: '',
        dialogUpdateDate: false,
        dialogUpdateTaskDateInfo: {
          taskId: '',
          dateType: '',
          dateChange: '',
          startDate: '',
          dueDate: '',
          prototypeId: '',
          note: ''
        },
        getPhotoUrl,
        userRole: null,
        currentStockValue: 0,
        dialogBulkStageDate: false,
        stagePreSelectedId: '',
        copySourceId: null,
        copyTargetIds: [],
        copyNoteDialog: false,
        copyNote: '',
        copySaving: false,
        importTaskDateDialog: false,
        importTaskDateSaving: false,
        customFields: [],
        customFieldValues: {},
        customFieldSaved: {}
      }
  },
  computed: {
    designersWithHeader () {
      const designers = this.selectOptions.designers || []

      return this.filterSquadMember(designers)
    },
    creativesWithHeader () {
      const creative = this.selectOptions.stylists || []

      return this.filterSquadMember(creative)
    },
    merchandisersWithHeader () {
      const merchandisers = this.selectOptions.merchandisers || []

      return this.filterSquadMember(merchandisers)
    },
    graphicsWithHeader () {
      const graphics = this.selectOptions.graphics || []

      return this.filterSquadMember(graphics)
    },
    marketingsWithHeader () {
      const marketings = this.selectOptions.marketings || []

      return this.filterSquadMember(marketings)
    },
    visualMerchandiserWithHeader () {
      const visualMerchandiser = this.selectOptions.visualMerchandisers || []

      return this.filterSquadMember(visualMerchandiser)
    },
    producersWithHeader () {
      const producers = this.selectOptions.producers || []

      return this.filterSquadMember(producers)
    },
    activeTimelines () {
      return this.workflow?.timelines.filter((timeline) => timeline.status === 'active') ?? []
    },
    isControllerUser () {
      return ['merchandising_planner', 'developer', 'management'].includes(this.userRole)
    },
    isCanSelectTimeline () {
      return ['merchandising_planner', 'developer', 'management', 'merchandiser'].includes(this.userRole)
    },
    isCopyMode () {
      return !!this.copySourceId
    },
    stageColumns () {
      return (this.workflow?.stages ?? []).map((stage) => {
        const formattedPic = stage.pic === 'creative' ? 'Creative / Stylist' : stage.pic
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
        return { id: stage.id, label: `${stage.name} (${formattedPic})` }
      })
    },
    copySourcePrototype () {
      return this.prototypes.find((p) => p.id === this.copySourceId) ?? null
    },
    copyTargetCandidateIds () {
      return this.prototypes
        .filter((p) => p.id !== this.copySourceId)
        .map((p) => p.id)
    },
    allTargetsSelected () {
      return this.copyTargetCandidateIds.length > 0
        && this.copyTargetCandidateIds.every((id) => this.copyTargetIds.includes(id))
    },
    someTargetsSelected () {
      return this.copyTargetIds.length > 0
    },
    legend () {
      return [
        { style: TASK_STATUS_STYLES.completed_on_time, label: 'Completed' },
        { style: TASK_STATUS_STYLES.critical, label: 'Critical' },
        { style: TASK_STATUS_STYLES.overdue, label: 'Overdue' },
        { style: TASK_STATUS_STYLES.dueIn0, label: 'Due Soon' },
        { style: TASK_STATUS_STYLES.inProgress, label: 'In Progress' },
        { style: TASK_STATUS_STYLES.pending, label: 'Pending' }
      ]
    }
  },
  async mounted () {
    this.initLoading = true
    this.userRole = getRole()
    await this.initSelectOptions()
    await this.getWorkFlow()
    await this.getCustomFields()
    this.initLoading = false
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    get (obj, path, fallback = '-') {
      try {
        return path.split('.').reduce((o, i) => o[i], obj) ?? fallback
      } catch (_) {
        return fallback
      }
    },
    worstStyleFor (prototype) {
      const summaries = Object.values(prototype.tasksByStageDisplay || {}).filter(Boolean)
      if (!summaries.length) return null
      const worstKey = OPEN_SEVERITY.find((key) => summaries.some((summary) => summary === TASK_STATUS_STYLES[key]))
      return worstKey ? TASK_STATUS_STYLES[worstKey] : TASK_STATUS_STYLES.completed_on_time
    },
    hasChangedDateTask (prototype) {
      return Object.values(prototype.tasksByStage || {})
        .some((task) => task && task.changeDateAt)
    },
    poQtyText (po, sketchId) {
      const target = (sketchId || '').trim().toUpperCase()
      if (!target) return ''
      const lines = (po.purchaseOrderLines || []).filter(
        (line) => (line.description || '').trim().toUpperCase() === target
      )
      if (!lines.length) return ''
      const quantity = lines.reduce((sum, line) => sum + (line.quantity || 0), 0)
      const receivedQty = lines.reduce((sum, line) => sum + (line.receivedQty || 0), 0)
      return ` (${receivedQty}/${quantity})`
    },
    stageCardStyle (status) {
      const accents = {
        'In Progress': '#3f51b5',
        'Due Today': '#ff9800',
        'Due Tomorrow': '#ff9800',
        'Due in 2 Days': '#ff9800',
        'Overdue': '#f44336'
      }
      const color = accents[status]
      if (!color) return {}
      // inset box-shadow draws the accent ring without adding to the box's layout size (a real border here
      // was found to throw off the absolutely-positioned card's height and clip its content in some rows).
      return { boxShadow: `inset 0 0 0 1.5px ${color} !important` }
    },
    deliveryPillStyle (status) {
      const map = {
        on_time: { bg: '#e8f5e9', color: '#2e7d32' },
        within_month: { bg: '#fff3e0', color: '#ef6c00' },
        late: { bg: '#ffebee', color: '#c62828' }
      }
      const found = map[status]
      if (!found) return {}
      return { background: `${found.bg} !important`, color: `${found.color} !important` }
    },
    async getWorkFlow () {
      const { data } = await this.productionPlanService.getWorkflow()
      if (data) {
        this.workflow = data
        this.workflow.stages = data.stages.filter((stage) => stage.status === 'active')?.sort((stageA, stageB) => (stageA.order - stageB.order)) ?? []
        this.workflow.stages
          .forEach((stage, index) => {
          const formattedPic = stage.pic === 'creative' ? 'Creative / Stylist' : stage.pic
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
            this.headers.push({
              text: `${stage.name} (${formattedPic})`,
              value: `masterStage${index}`,
              align: 'center',
              width: 250,
              stageId: stage.id
            })
          })
        this.headers.push(...this.tailHeaders)
      }
    },
    async getCustomFields () {
      try {
        const { data } = await this.productionPlanService.getCustomFields()
        const fields = Array.isArray(data) ? data : (data?.results ?? [])
        this.customFields = fields
          .filter((field) => field.status === 'active')
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }))

        this.customFields.forEach((field) => {
          this.headers.push({
            text: field.label || field.name,
            value: `customField:${field.id}`,
            align: 'center',
            width: 200,
            isCustom: true
          })
        })
      } catch (error) {
        console.error('getCustomFields', error)
        this.setSnackbar({
          value: true,
          message: `ERROR WHILE LOAD CUSTOM FIELDS: ${error.message}`,
          type: 'error'
        })
      }
    },
    getCustomFieldValue (prototypeId, fieldId) {
      return this.customFieldValues[prototypeId]?.[fieldId] ?? ''
    },
    getCustomFieldSavedValue (prototypeId, fieldId) {
      return this.customFieldSaved[prototypeId]?.[fieldId] ?? ''
    },
    isCustomFieldDirty (prototypeId, fieldId) {
      return String(this.getCustomFieldValue(prototypeId, fieldId)) !== String(this.getCustomFieldSavedValue(prototypeId, fieldId))
    },
    updateCustomFieldValue (prototypeId, fieldId, value) {
      if (!this.customFieldValues[prototypeId]) {
        this.$set(this.customFieldValues, prototypeId, {})
      }
      this.$set(this.customFieldValues[prototypeId], fieldId, value)
    },
    hydrateCustomFieldValues (prototype) {
      const attrs = prototype?.customAttributes || {}
      const values = {}
      this.customFields.forEach((field) => {
        values[field.id] = attrs[field.name] ?? ''
      })
      this.$set(this.customFieldValues, prototype.id, { ...values })
      this.$set(this.customFieldSaved, prototype.id, { ...values })
    },
    async saveCustomField (prototype, field) {
      if (!this.isCustomFieldDirty(prototype.id, field.id)) return

      const value = this.getCustomFieldValue(prototype.id, field.id)

      try {
        await this.prototypeService.updateCustomAttribute({
          prototypeId: prototype.id,
          name: field.name,
          value
        })

        if (!this.customFieldSaved[prototype.id]) {
          this.$set(this.customFieldSaved, prototype.id, {})
        }
        this.$set(this.customFieldSaved[prototype.id], field.id, value)

        this.setSnackbar({
          value: true,
          message: `บันทึก ${field.label || field.name} แล้ว`,
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'บันทึก custom field ผิดพลาด',
          type: 'error'
        })
      }
    },
    async initSelectOptions () {
      try {
        const promised = await Promise.all([
          PrototypeAttributeService.getSquadMember({
            page: 1,
            itemsPerPage: 99999,
            sortBy: ['id'],
            sortDesc: [true]
          }),
          FactoryService.getAll()
        ])

        const users = promised[0].data.results

        const jobMap = {
          designer: 'designers',
          merchandiser: 'merchandisers',
          graphic: 'graphics',
          creative: 'stylists',
          visual_merchandiser: 'visualMerchandisers',
          marketing: 'marketings',
          producer: 'producers'
        }

        const normalize = (item) => ({
          id: item.id,
          status: item.status,
          name: item.name,
          squad: item.squad
        })

        Object.values(jobMap).forEach((key) => {
          this.selectOptions[key] = []
        })

        users.forEach((item) => {
          const key = jobMap[item.job]
          if (key) {
            this.selectOptions[key].push(normalize(item))
          }
        })
        this.selectOptions.factories = promised[1].data.results
          .map(normalize)
          .sort((a, b) => a.id - b.id)
      } catch (error) {
          console.error('initSelectOptions', error)
          this.setSnackbar({
            value: true,
            message: `ERROR WHILE INIT OPTIONS: ${error.message}`,
            type: 'error'
          })
      }
    },
    async getCollectionPlaning (collectionId, collectionName) {
      if (!this.workflow) {
        this.setSnackbar({
          value: true,
          message: 'กรุณาสร้าง workflow ก่อนดำเนินการต่อ',
          type: 'error'
        })
      }
      this.loadingPrototype = true
      const { data } = await this.productionPlanService.getCollectionPlaning({
        collectionId,
        collectionName
      })
      if (data) {
        this.prototypes = data
        this.currentStockValue = 0
        this.prototypes = data.map((prototype) => {
          const tasksByStage = Object.fromEntries(
            prototype.tasks.map((t) => [t.stageId, t])
          )

          const tasksByStageDisplay = {}
          Object.keys(tasksByStage).forEach((stageId) => {
            tasksByStageDisplay[stageId] = calculateTaskStatus(
              tasksByStage[stageId],
              this.status
            )
          })

          this.pushSquadMemberDel('designer', 'designers', prototype.sketch)
          this.pushSquadMemberDel('stylist', 'stylists', prototype.sketch)
          this.pushSquadMemberDel('merchandiser', 'merchandisers', prototype.sketch)
          this.pushSquadMemberDel('graphic', 'graphics', prototype.sketch)
          this.pushSquadMemberDel('marketing', 'marketings', prototype.sketch)
          this.pushSquadMemberDel('visualMerchandiser', 'visualMerchandisers', prototype.sketch)
          this.pushSquadMemberDel('producer', 'producers', prototype.sketch)

          const stockValue = (prototype?.order?.colors?.reduce((s, n) => s + (n?.totalQty ?? 0), 0) ?? 0) * (prototype?.order?.sellPrice ?? 0)
          this.currentStockValue += stockValue

          this.hydrateCustomFieldValues(prototype)

          return {
            ...prototype,
            stockValue,
            deliveryStatus: prototype?.deliveryStatus ?? '',
            tasksByStage,
            tasksByStageDisplay
          }
        })
      }
      this.loadingPrototype = false
    },
    onUnComplete (prototypeId, taskId) {
      if (taskId) {
        this.$store.dispatch('Components/setModal', {
          value: true,
          title: 'เปลี่ยนสถานะ',
          message: 'ยืนยันการเปลี่ยนสถานะหรือไม่',
          confirmText: 'ยืนยัน',
          confirmType: 'success',
          cancelType: '',
          cancelText: 'ยกเลิก',
          persistent: false,
          onConfirm: () => this.updateTaskStatus(prototypeId, taskId, 'pending')
        })
      }
    },
    onSelectCollection (val) {
      this.prototypes = []
      this.selectedCollection = val ?? null

      if (val?.id && String(val.id) !== this.$route.params.collectionId) {
        this.$router.replace({
          name: 'TimeAndAction',
          params: { collectionId: String(val.id) }
        }).catch(() => {})
      }

      this.getCollectionPlaning(val.id, val.name)
    },
    filterSquadMember (job) {
      if (!Array.isArray(job) || job.length === 0) return []

      const currentSquad = this.selectedCollection?.squad?.name || null

      return job
        .slice()
        .sort((a, b) => {
          const squadA = a.squad?.name || null
          const squadB = b.squad?.name || null

          if (currentSquad) {
            if (squadA === currentSquad && squadB !== currentSquad) return -1
            if (squadA !== currentSquad && squadB === currentSquad) return 1
          }

          if (!squadA && squadB) return 1
          if (squadA && !squadB) return -1

          return (squadA || '').localeCompare(squadB || '')
        })
        .reduce((acc, item) => {
          const squadName = item.squad?.name || 'No Squad'

          if (!acc.some((e) => e.header === squadName)) {
            acc.push({ header: squadName })
          }

          acc.push(item)
          return acc
        }, [])
    },
    async updateSketchAssignee (id, field, value) {
      try {
        await this.prototypeService.updateSketchAssignee({
          prototypeId: id,
          field,
          value
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: 'การระบุข้อมูลผิดพลาด',
          type: 'error'
        })
      } finally {
        await this.getPrototypeTasks(id)
      }
    },
    async updateDeliveryStatus (id, status) {
      try {
        await this.prototypeService.updateDeliveryStatus({
          prototypeId: id,
          status
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: 'การระบุข้อมูลผิดพลาด',
          type: 'error'
        })
      } finally {
        await this.getPrototypeTasks(id)
      }
    },
    async assignTimeline (prototypeId, sketchId, timelineId) {
      try {
        const payload = {
          workflowId: this.workflow.id,
          timelineId,
          prototypeId,
          sketchId
        }
        await this.productionPlanService.assignTimeline(payload)
         this.setSnackbar({
          value: true,
          message: 'Timeline Updated',
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'การ Update Timeline ผิดพลาด',
          type: 'error'
        })
      } finally {
        this.getPrototypeTasks(prototypeId)
      }
    },
    async handleUpdateDate ({ taskId, dateType, dateChange, prototypeId, note }) {
      try {
        const payload = {
         taskId,
         dateType,
         dateChange: this.$dayjs(dateChange).startOf('day').toISOString(),
         startDate: this.dialogUpdateTaskDateInfo.startDate,
         dueDate: this.dialogUpdateTaskDateInfo.dueDate,
         note
        }
        await this.productionPlanService.updateTaskDate(payload)
        this.setSnackbar({
          value: true,
          message: `${dateType === 'dueDate' ? 'Due date' : 'Start date'} updated`,
          type: 'success'
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: 'การระบุข้อมูลผิดพลาด',
          type: 'error'
        })
      } finally {
        await this.getPrototypeTasks(prototypeId)
      }
    },
    openDialogTaskDate (prototypeId, taskInfo, dateType) {
        this.dialogUpdateTaskDateInfo = {
          taskId: taskInfo.id,
          dateType,
          dateChange: taskInfo[dateType],
          startDate: taskInfo.startDate,
          dueDate: taskInfo.dueDate,
          prototypeId
        }
        this.dialogUpdateDate = true
    },
    clearDialogUpdateTaskDateInfo () {
      this.dialogUpdateTaskDateInfo = {
          taskId: '',
          dateType: '',
          dateChange: '',
          startDate: '',
          dueDate: '',
          prototypeId: ''
      }
    },
    async updateTaskStatus (prototypeId, taskId, changeStatusTo) {
      try {
        await this.productionPlanService.updateTaskStatus({
          taskId,
          status: changeStatusTo
        })
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: 'การเปลี่ยนสถานะผิดพลาด',
          type: 'error'
        })
      } finally {
        await this.getPrototypeTasks(prototypeId)
      }
    },
    async getPrototypeTasks (prototypeId) {
      try {
        const { data: prototypeInfo } = await this.productionPlanService.getPrototypeTasks(prototypeId)

        if (!prototypeInfo) return

        const prototypeIndex = this.prototypes.findIndex((prototype) => prototype.id === prototypeId)

        if (prototypeIndex !== -1) {
          const tasksByStage = Object.fromEntries(
            prototypeInfo.tasks.map((t) => [t.stageId, t])
          )
          const tasksByStageDisplay = {}
          Object.keys(tasksByStage).forEach((stageId) => {
            tasksByStageDisplay[stageId] = calculateTaskStatus(
              tasksByStage[stageId],
              this.status
            )
          })

          this.pushSquadMemberDel('designer', 'designers', prototypeInfo.sketch)
          this.pushSquadMemberDel('stylist', 'stylists', prototypeInfo.sketch)
          this.pushSquadMemberDel('merchandiser', 'merchandisers', prototypeInfo.sketch)
          this.pushSquadMemberDel('graphic', 'graphics', prototypeInfo.sketch)
          this.pushSquadMemberDel('marketing', 'marketings', prototypeInfo.sketch)
          this.pushSquadMemberDel('visualMerchandiser', 'visualMerchandisers', prototypeInfo.sketch)
          this.pushSquadMemberDel('producer', 'producers', prototypeInfo.sketch)

          const stockValue = (prototypeInfo?.order?.colors?.reduce((s, n) => s + (n?.totalQty ?? 0), 0) ?? 0) * (prototypeInfo?.order?.sellPrice ?? 0)
          this.currentStockValue -= this.prototypes[prototypeIndex].stockValue
          this.currentStockValue += stockValue

          this.$set(this.prototypes, prototypeIndex, {
            ...prototypeInfo,
            stockValue,
            deliveryStatus: prototypeInfo?.deliveryStatus ?? '',
            tasks: prototypeInfo.tasks ?? [],
            tasksByStage,
            tasksByStageDisplay
          })
        }
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: 'การดึงข้อมูลผิดพลาด',
          type: 'error'
        })
      }
    },
    formatExportStatus (status) {
      if (status?.status?.toLowerCase() === 'completed') {
        return `${status.status} (${status.extendText})`
      }
      if (status && status?.status?.toLowerCase() !== 'completed') {
        return 'Incomplete'
      }

      return 'No Task'
    },
    async exportCSV () {
      const exportInfo = this.prototypes.map((prototype) => {
        const allStage = {}
        for (const stage of this.workflow.stages) {
          const formattedPic = stage.pic === 'creative' ? 'Creative / Stylist' : stage.pic
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
          const task = prototype?.tasksByStage?.[stage.id]
          const status = this.formatExportStatus(prototype?.tasksByStageDisplay?.[stage.id])
          allStage[`${stage.name} (${formattedPic})`] = task?.dueDate
            ? `${this.$dayjs(task.dueDate).locale('en').format('ddd, DD MMMM YYYY')} - ${status}`
            : status
        }

        return {
          'Image': getPhotoUrl(prototype),
          'Sketch ID': prototype.sketch.sketchId,
          'Timeline': prototype?.timeline?.name ?? 'NO TIMELINE',
          'Delivery Status': prototype?.deliveryStatus ? capitalize(prototype.deliveryStatus.split('_').join(' ')) : 'ไม่ระบุ',
          ...allStage,
          'Factory': prototype?.sketch?.factory?.name ?? '',
          'Designer': prototype?.sketch?.designer?.name ?? '',
          'Merchandiser': prototype?.sketch?.merchandiser?.name ?? '',
          'Graphic': prototype?.sketch?.graphic?.name ?? '',
          'Creative / Stylist': prototype?.sketch?.stylist?.name ?? '',
          'Visual Merchandiser': prototype?.sketch?.visualMerchandiser?.name ?? '',
          'Marketing': prototype?.sketch?.marketing?.name ?? '',
          'Producer': prototype?.sketch?.producer?.name ?? '',
          'Stock Value': this.$options.filters.showFullPriceFormat(prototype.stockValue) ?? 0
        }
      })
        const timeStringFormat = this.$dayjs().format('DD_MM_YYYY_HHmm')
        const options = {
          filename: `${this.selectedCollection.name}_timeline_${timeStringFormat}`,
          showLabels: true,
          useKeysAsHeaders: true
        }

        const csvExporter = new ExportToCsv(options)
        csvExporter.generateCsv(exportInfo)
    },
    exportForUpdate () {
      const formatDate = (date) => (date ? this.$dayjs(date).format('DD/MM/YYYY') : '')
      const prototypesWithTimeline = this.prototypes.filter((prototype) => prototype?.timeline)
      if (!prototypesWithTimeline.length) {
        this.setSnackbar({
          value: true,
          message: 'ไม่มี Sketch ที่มี Timeline สำหรับ export',
          type: 'warning'
        })
        return
      }

      const exportInfo = prototypesWithTimeline.map((prototype) => {
        const allStage = {}
        for (const stage of this.stageColumns) {
          const task = prototype?.tasksByStage?.[stage.id]
          allStage[`${stage.label} - Start`] = task ? formatDate(task.startDate) : 'No Task'
          allStage[`${stage.label} - Due`] = task ? formatDate(task.dueDate) : 'No Task'
        }

        return {
          'Sketch ID': prototype.sketch.sketchId,
          'Timeline': prototype.timeline.name,
          ...allStage
        }
      })
      const timeStringFormat = this.$dayjs().format('DD_MM_YYYY_HHmm')
      const options = {
        filename: `${this.selectedCollection.name}_for_update_${timeStringFormat}`,
        showLabels: true,
        useKeysAsHeaders: true
      }

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(exportInfo)
    },
    async handleImportTaskDate ({ tasks, note }) {
      this.importTaskDateSaving = true
      try {
        const { data } = await this.productionPlanService.batchUpdateTaskDate({
          tasks: tasks.map((task) => ({ ...task, note })),
          source: 'import'
        })
        this.setSnackbar({
          value: true,
          message: `Import สำเร็จ (${data?.updatedCount ?? tasks.length} tasks)`,
          type: 'success'
        })
        this.importTaskDateDialog = false
        await this.getCollectionPlaning(this.selectedCollection.id, this.selectedCollection.name)
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'Import ผิดพลาด',
          type: 'error'
        })
      } finally {
        this.importTaskDateSaving = false
      }
    },
    showPo (poId) {
      if (!poId) return

      const routeData = this.$router.resolve({
        name: 'DeliveryNoteOfPurchaseOrder',
        params: { id: poId }
      })
      window.open(routeData.href, '_blank')
    },
    pushSquadMemberDel (job, field, sketch) {
      if (sketch[job]?.id && !this.selectOptions[field].some((item) => item.id === sketch[job].id)) {
        this.selectOptions[field].push({
          id: sketch[job]?.id,
          name: sketch[job]?.name,
          squad: { name: 'Deleted', id: 'deleted', status: 'deleted' },
          status: 'deleted'
        })
      }
    },
    openStageDateDialog (stageId) {
      this.stagePreSelectedId = stageId
      this.dialogBulkStageDate = true
    },

    async handleStageDateUpdate ({ stageId, startDate, dueDate, note }) {
      const tasksToUpdate = []
      this.prototypes.forEach((prototype) => {
        const task = prototype.tasksByStage[stageId]
        if (task && !task.completedAt) {
          tasksToUpdate.push({
            taskId: task.id,
            prototypeId: prototype.id
          })
        }
      })

      if (!tasksToUpdate.length) {
        this.setSnackbar({
          value: true,
          message: 'ไม่มี Task ที่สามารถอัปเดตได้ใน Stage นี้',
          type: 'warning'
        })
        return
      }

      this.loadingPrototype = true

      try {
        const payload = {
          taskIds: tasksToUpdate.map((t) => t.taskId),
          note
        }
        if (startDate) {
          payload.startDate = this.$dayjs(startDate).startOf('day').toISOString()
        }
        if (dueDate) {
          payload.dueDate = this.$dayjs(dueDate).startOf('day').toISOString()
        }

        await this.productionPlanService.bulkUpdateTaskDate(payload)

        this.setSnackbar({
          value: true,
          message: `อัปเดต Stage dates สำเร็จ (${tasksToUpdate.length} tasks)`,
          type: 'success'
        })

        await Promise.all(
          tasksToUpdate.map((task) => this.getPrototypeTasks(task.prototypeId))
        )
      } catch (error) {
        console.error('handleUpdateStageDates :', error)
        this.setSnackbar({
          value: true,
          message: `อัปเดต Stage dates ผิดพลาด: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loadingPrototype = false
      }
    },
    resolveFactory (prototype) {
      if (prototype.order?.factory) {
        return prototype.order.factory
      }
      if (prototype.sketch?.factory) {
        return prototype.sketch.factory
      }
      return null
    },
    toggleCopySource (prototype) {
      this.copyTargetIds = []
      this.copySourceId = this.copySourceId === prototype.id ? null : prototype.id
    },
    cancelCopyTimeline () {
      this.copySourceId = null
      this.copyTargetIds = []
    },
    toggleCopyTarget (prototypeId) {
      this.copyTargetIds = this.copyTargetIds.includes(prototypeId)
        ? this.copyTargetIds.filter((id) => id !== prototypeId)
        : [...this.copyTargetIds, prototypeId]
    },
    toggleSelectAllTargets () {
      this.copyTargetIds = this.allTargetsSelected ? [] : [...this.copyTargetCandidateIds]
    },
    openCopyNoteDialog () {
      this.copyNote = ''
      this.copyNoteDialog = true
    },
    async confirmCopyTimeline () {
      if (!this.copyNote || !this.copySourcePrototype) return

      const sourceStages = this.copySourcePrototype.tasksByStage
      const tasks = []

      this.copyTargetIds.forEach((targetId) => {
        const target = this.prototypes.find((p) => p.id === targetId)
        if (!target) return

        Object.keys(sourceStages).forEach((stageId) => {
          const targetTask = target.tasksByStage[stageId]
          if (!targetTask) return

          tasks.push({
            id: targetTask.id,
            startDate: sourceStages[stageId].startDate,
            dueDate: sourceStages[stageId].dueDate,
            note: this.copyNote
          })
        })
      })

      if (!tasks.length) {
        this.setSnackbar({
          value: true,
          message: 'ไม่มี Task ที่สามารถ copy ได้',
          type: 'warning'
        })
        this.copyNoteDialog = false
        return
      }

      this.copySaving = true
      try {
        const { data } = await this.productionPlanService.batchUpdateTaskDate({ tasks, source: 'copy_timeline' })
        this.setSnackbar({
          value: true,
          message: `Copy Timeline สำเร็จ (${data?.updatedCount ?? tasks.length} tasks)`,
          type: 'success'
        })
        this.copyNoteDialog = false
        await Promise.all(this.copyTargetIds.map((id) => this.getPrototypeTasks(id)))
        this.cancelCopyTimeline()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'Copy Timeline ผิดพลาด',
          type: 'error'
        })
      } finally {
        this.copySaving = false
      }
    }
  }
}
</script>

<style scoped>
.tna-page {
  padding: 10px 10px 0;
  background: #f2f4f5;
  min-height: calc(100vh - 70px);
}

.tna-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0px 3px 3px -2px rgba(0, 0, 0, 0.06), 0px 3px 4px 0px rgba(0, 0, 0, 0.05), 0px 1px 8px 0px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.tna-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
}

.tna-subtitle {
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}

.tna-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tna-stock-value {
  text-align: right;
}

.tna-stock-label {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tna-stock-amount {
  font-size: 16px;
  font-weight: 700;
  color: #333333;
}

.tna-stock-target {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.4);
}

.tna-legend {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 18px;
  background: #1c2b30;
  flex-wrap: wrap;
}

.tna-legend-title {
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.tna-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
}

.tna-legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 4px;
  display: inline-block;
}

.tna-legend-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.tna-table-scroll {
  height: 100%;
  overflow-y: scroll;
  overflow-x: auto;
}

.tna-stage-card {
  border-radius: 10px !important;
  padding: 8px 10px !important;
  overflow: hidden !important;
}

.tna-stage-card .v-icon {
  font-size: 18px !important;
}

.tna-stage-card h3 {
  font-size: 12px !important;
  line-height: 1.3 !important;
  margin-top: 0 !important;
  margin-bottom: 3px !important;
}

.tna-stage-card p,
.tna-stage-card a {
  font-size: 11px !important;
  line-height: 1.4 !important;
  margin-top: 0 !important;
  margin-bottom: 3px !important;
}

.tna-stage-card p.mb-0 {
  margin-bottom: 0 !important;
}

.tna-stage-card .mdi-pencil-circle-outline {
  font-size: 15px !important;
}

.tna-stage-card--empty {
  background: #eeeeee;
}

.tna-pill-select {
  border-radius: 999px;
  background: #f2f4f5;
  overflow: hidden;
}

.tna-pill-select ::v-deep .v-input__slot {
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  min-height: 34px !important;
  padding: 0 14px !important;
  justify-content: center;
}

.tna-pill-select ::v-deep .v-text-field__details {
  display: none !important;
}

.tna-pill-select ::v-deep .v-label,
.tna-pill-select ::v-deep fieldset {
  display: none !important;
}

.tna-pill-select ::v-deep .v-select__selection,
.tna-pill-select ::v-deep input {
  font-size: 12px !important;
  font-weight: 600;
  color: inherit;
}

.tna-pill-select ::v-deep .v-input__icon .v-icon {
  font-size: 16px !important;
}

.tna-stock-cell {
  font-weight: 600;
  color: #333333;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.table th {
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 8px;
  vertical-align: top;
  position: sticky;
  top: 0;
  background: white;
  z-index: 101;
}
.table td {
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  padding: 8px;
  vertical-align: top;
}

tbody tr:hover td {
  background-color: rgb(244, 244, 255);
}

.border-secondary {
  border: 2px solid var(--v-secondary-base);
  border-radius: 50%;
  background: white;
  padding: 2px;
}

.table-wrapper {
  overflow-x: auto;
  position: relative;
}

.sticky-col {
  position: sticky;
  background: white;
  z-index: 100;
}

thead .sticky-col {
  z-index: 102;
}

.col-0 {
  left: 0;
}

.col-1 {
  left: 30px;
}

.col-2 {
  left: 130px;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
  clip-path: inset(0px -10px 0px 0px);
}

.tna-status-cell {
  width: 30px;
  padding: 0 !important;
}

.main-image {
  width: 60px;
}
.td-cell {
  position: relative;
  vertical-align: stretch;
}

.td-fill {
  display: flex;
  flex: 1;
  height: 100%;
}

.td-fill > div {
  flex: 1;
  display: flex;
}
.td-flow {
  min-height: 116px;
  height: 100%;
  display: flex;
}
.po-link {
  cursor: pointer;
  color: #1976d2;
  text-decoration: underline;
}
.po-link:hover {
  opacity: 0.8;
}
.source-row {
  background: #e3f2fd;
}
.source-row .sticky-col {
  background: #e3f2fd;
}
</style>
